import { useState, useEffect } from 'react'
import { useParams } from 'react-router'
import classNames from 'classnames'

import { withRouter } from 'i18n/utils'
import { CardSection } from 'shared/components/CardSection'
import ErrorMessage from 'shared/components/ErrorMessage'

import Button from 'shared/components/Button'
import featureToggles from 'shared/featureToggles'
import { sendAmplitudeEvent, useSendGTAGEvent } from 'analytics/utils'

import { SHOW_HOURLY_FLOW } from 'booking/constants'

import withRates from '../../../../shared/hocs/withRates'
import { CARDS } from 'booking/reducers'

import { clearPending } from 'api/actions'
import {
    upgradeUpSellBookingDetails,
    fetchUpSellOptions,
    selectUpSellOption,
    clearSelectedUpSellOption,
    getAdditionalParkingItems,
} from 'booking/actions'
import {
    selectedUpSellOptionSelector,
    upSellOptionsSelector,
    upgradeUpSellOptionPendingSelector,
    upgradeUpSellErrorSelector,
    getUpSellOptionPendingSelector,
    additionalParkingItemsSelector,
    addtionalItemsPendingSelector,
} from 'booking/selectors'

import UpSellModal from './UpSellModal'
import { ProductCardSection } from './ProductCardSection'
import AdditionalItemsModal from './AdditionalItemsModal'

import styles from '../styles.module.scss'
import { reservationViaUrlSelector } from 'dashboard-transactions/selectors'
import { withRouteParams } from 'shared/hoc'

const ProductSelection = ({
    filteredRates,
    onNext,
    setProduct,
    onRateSelected,
    selectedRateId,
    disabledCards,
    ratesError,
    areRatesPending,
    fetchUpSellOptions,
    upgradeUpSellBookingDetails,
    upSellOptions,
    selectUpSellOption,
    selectedUpSellOption,
    clearSelectedUpSellOption,
    areUpgradeUpSellOptionPending,
    upgradeUpSellPendingError,
    clearPending,
    areGetUpSellOptionPending,
    getAdditionalParkingItems,
    additionalParkingItems,
    setAdditionalServices,
    bookingData,
    areAddtionalItemsPending,
    existingReservation,
    currentCustomerFlow,
    nonBookable,
    analyticsFlowType,
}) => {
    const [openUpSellModal, setOpenUpSellModal] = useState(false)
    const [openAdditionalServiceModal, setOpenAdditionalServiceModal] = useState(false)
    const [cancelBtnLoading, setCancelBtnLoading] = useState(false)
    const [selectedUpSell, setSelectedUpSell] = useState(null)
    const [selectedAdditionalService, setSelectedAdditionalService] = useState([])
    const [isAdditionalServicesLoaded, setIsAdditionalServicesLoaded] = useState(false)
    const sendGTAGEvent = useSendGTAGEvent()
    const params = useParams()

    // If url params contain locationId, then it means we are in Edit flow
    // and in edit flow, we shouldn't show upsell modal
    const showUpSellModal = featureToggles.hasUpSellFlow && upSellOptions?.length > 0 && !params.reservationId
    const showAdditionalServiceModal = featureToggles.hasAdditionalProducts && additionalParkingItems?.length > 0

    const rates = filteredRates.filter(rate => {
        const defaultCustomerFlowArr = rate.defaultCustomerFlow.split('|')

        if (existingReservation && Object.keys(existingReservation).length > 0) {
            return rate.rateId === existingReservation.rateId
        }
        if (currentCustomerFlow) {
            return defaultCustomerFlowArr.includes(currentCustomerFlow)
        } else {
            return !!defaultCustomerFlowArr.some(element => SHOW_HOURLY_FLOW.includes(element))
        }
    })

    useEffect(() => {
        clearSelectedUpSellOption()
        setIsAdditionalServicesLoaded(false)
    }, [])

    useEffect(() => {
        setIsAdditionalServicesLoaded(false)
        receiveUpSellOptions()
        if (
            !existingReservation &&
            featureToggles.hasAdditionalProducts &&
            selectedRateId &&
            (!hasRateUpSellOptions(selectedRateId) || !featureToggles.hasUpSellFlow)
        ) {
            getAdditionalServices()
        }
    }, [selectedRateId])

    useEffect(() => {
        if (selectedUpSell && selectedUpSellOption) {
            onUpgradeRate()
        }
    }, [selectedUpSellOption])

    useEffect(() => {
        if (additionalParkingItems) {
            setIsAdditionalServicesLoaded(true)
        }
    }, [additionalParkingItems])

    useEffect(() => {
        if (isAdditionalServicesLoaded) {
            selectedRateId && openUpSellModal && !selectedUpSellOption
                ? onUpsellOptionCancel()
                : onUpsellOptionUpgraded()
        }
    }, [isAdditionalServicesLoaded])

    useEffect(() => {
        if (bookingData.additionalServices?.items?.length > 0) {
            setProduct({ ...selectedRateId, displayValue: selectedRateId.rateName })
            onNext()
        }
    }, [bookingData?.additionalServices])

    const selectedItemsResult = () =>
        `${selectedAdditionalService.map(item => `${item.id}|${item.value}|1|0`).join(',')}`

    const activeSelectedRate = rate => selectedRateId && rate.$rateId === selectedRateId.$rateId

    const hasRateUpSellOptions = rate => rate?.rateUpSellOptions?.length > 0

    const receiveUpSellOptions = () => {
        // If url params contain locationId, then it means we are in Edit flow
        // and in edit flow, we don't show upsell modal -> no point of fetching data
        !params.reservationId &&
            selectedRateId &&
            featureToggles.hasUpSellFlow &&
            hasRateUpSellOptions(selectedRateId) &&
            fetchUpSellOptions(
                selectedRateId.rateId,
                selectedRateId.locationId,
                selectedRateId.actualFromDate,
                selectedRateId.actualToDate
            )
    }

    const onUpgradeRate = () => {
        upgradeUpSellBookingDetails(
            selectedRateId?.locationId,
            selectedUpSell.upSellRateId,
            selectedRateId.actualFromDate,
            selectedRateId.actualToDate,
            selectedRateId.rateId
        )
    }

    const onUpsellOptionUpgraded = () => {
        if (selectedUpSell && selectedUpSell?.upSellRateId === Number(selectedRateId.rateId)) {
            setProduct({ ...selectedRateId, displayValue: selectedRateId.rateName })
            sendGTAGEvent('upgrade_selected', {
                upgrade_product_name: selectedUpSell.upSellRateName,
                upsell_rate_id: selectedUpSell.upSellRateId,
                upgrade_amount: selectedUpSell.additionalAmount,
                flow_type: analyticsFlowType,
            })

            sendAmplitudeEvent('Upgrade Selected', {
                'Upgrade Product Name': selectedUpSell.upSellRateName,
                'Upsell Rate ID': selectedUpSell.upSellRateId,
                'Upgrade Amount': selectedUpSell.additionalAmount,
                'Flow Type': analyticsFlowType,
            })

            setOpenUpSellModal(false)
            if (featureToggles.hasAdditionalProducts && additionalParkingItems?.length > 0) {
                setOpenAdditionalServiceModal(true)
            } else onNext()
        }
    }

    const onUpsellOptionConfirm = () => {
        selectUpSellOption({
            locationId: selectedRateId.locationId,
            rateId: selectedUpSell.upSellRateId,
            beginTime: selectedRateId.actualFromDate,
            endTime: selectedRateId.actualToDate,
            startedWithProductId: selectedRateId.rateId,
        })
    }

    const onUpsellOptionCancel = () => {
        setProduct({ ...selectedRateId, displayValue: selectedRateId.rateName })
        setSelectedUpSell(null)
        clearPending(['upgradeUpSellOption'])
        setOpenUpSellModal(false)
        setCancelBtnLoading(false)
        if (featureToggles.hasAdditionalProducts) {
            additionalParkingItems.length > 0 ? setOpenAdditionalServiceModal(true) : onNext()
        } else {
            onNext()
        }
    }

    const onUpSellModalBackgroundClose = () => {
        clearPending(['upgradeUpSellOption'])
        setSelectedUpSell(null)
        setOpenUpSellModal(false)
        sendGTAGEvent('upgrade_declined', { flow_type: analyticsFlowType })
        sendAmplitudeEvent('Upgrade Declined', { 'Flow Type': analyticsFlowType })
    }

    const onUpsellModalCancelClick = () => {
        setCancelBtnLoading(true)
        sendGTAGEvent('upgrade_declined', { flow_type: analyticsFlowType })
        sendAmplitudeEvent('Upgrade Declined', { 'Flow Type': analyticsFlowType })
        getAdditionalServices()
    }

    const onUpSellOptionSelectHandler = option => {
        clearPending(['upgradeUpSellOption']), setSelectedUpSell(option)
    }

    const onClickHandler = rate => {
        onRateSelected(rate)
    }

    const toggleAdditionalServiceCheckbox = service => {
        const index = selectedAdditionalService.findIndex(item => item.id === service.id)
        if (index === -1) {
            setSelectedAdditionalService([...selectedAdditionalService, service])
        } else {
            const newSelectedAdditionalService = selectedAdditionalService.filter(item => item.id !== service.id)
            setSelectedAdditionalService(newSelectedAdditionalService)
        }
    }

    const getAdditionalServices = () => getAdditionalParkingItems(selectedRateId.rateId, selectedRateId.locationId)

    const onAdditionalServiceSelect = () => {
        setAdditionalServices({
            value: selectedItemsResult(),
            items: selectedAdditionalService,
        })
        setOpenAdditionalServiceModal(false)
    }

    const onAdditionalServiceCancel = () => {
        setOpenAdditionalServiceModal(false)
        setProduct({ ...selectedRateId, displayValue: selectedRateId.rateName })
        onNext()
    }

    const onAdditionalServiceBackdropCancel = () => {
        setSelectedAdditionalService([])
        setOpenAdditionalServiceModal(false)
        onUpSellModalBackgroundClose()
    }

    const onNextStep = () => {
        sendGTAGEvent('select_product', {
            product_name: selectedRateId.rateName,
            product_id: selectedRateId.rateId,
            price: selectedRateId.amount,
            flow_type: analyticsFlowType,
        })

        sendAmplitudeEvent('Selected Product', {
            'Product Name': selectedRateId.rateName,
            'Product ID': selectedRateId.rateId,
            Price: selectedRateId.amount,
            'Flow Type': analyticsFlowType,
        })

        if (showAdditionalServiceModal && selectedRateId && !hasRateUpSellOptions(selectedRateId)) {
            return setOpenAdditionalServiceModal(true)
        }
        if (showUpSellModal && hasRateUpSellOptions(selectedRateId) && selectedUpSell === null) {
            sendGTAGEvent('upgrade_popup_displayed', { flow_type: analyticsFlowType })
            sendAmplitudeEvent('Upgrade Popup Displayed', {
                'Flow Type': analyticsFlowType,
            })
            return setOpenUpSellModal(true)
        }

        if (!featureToggles.hasUpSellFlow || !hasRateUpSellOptions(selectedRateId) || !showUpSellModal) {
            return onNext(), setProduct({ ...selectedRateId, displayValue: selectedRateId.rateName })
        }
    }

    return (
        <>
            {(!disabledCards[CARDS.AVAILABLE_PRODUCTS] && ratesError) || (!areRatesPending && rates.length === 0) ? (
                <ErrorMessage messages={[ratesError ? ratesError : <T>booking.error.rates.not.found</T>]} />
            ) : (
                <CardSection className={styles.contentSection} loading={areRatesPending}>
                    <h3 className={classNames('text-uppercase', 'font-weight-bold', styles.dateLabel)}>
                        <T>booking.purchase.product.label</T>
                    </h3>
                    {nonBookable && (
                        <div>
                            <span className={'font-weight-bold text-brand-primary d-block'}>
                                <T>booking.products.step.nonbookable.step.title</T>
                            </span>
                            <span className="text-brand-primary">
                                <T>booking.products.step.nonbookable.step.description</T>
                            </span>
                        </div>
                    )}
                    {rates &&
                        rates.map((rate, i) => (
                            <ProductCardSection
                                index={i}
                                rate={rate}
                                activeSelectedRate={activeSelectedRate}
                                onClickHandler={onClickHandler}
                            />
                        ))}
                    {openUpSellModal && (
                        <UpSellModal
                            onClose={onUpSellModalBackgroundClose}
                            outsideClickDisabled={true}
                            rate={selectedRateId}
                            upSellOptions={upSellOptions}
                            selectedUpsellOption={selectedUpSell}
                            setSelectedUpSellOption={onUpSellOptionSelectHandler}
                            headerLabel={<T>booking.up.sell.modal.label</T>}
                            onConfirmClick={onUpsellOptionConfirm}
                            onCancelClick={onUpsellModalCancelClick}
                            onCancelButtonClickLabel={<T>booking.up.sell.modal.cancel.button.label</T>}
                            upgradeBtnIsLoading={areUpgradeUpSellOptionPending}
                            upgradeBtnError={upgradeUpSellPendingError}
                            onCancelBtnClickLoading={cancelBtnLoading}
                        />
                    )}
                    {openAdditionalServiceModal && (
                        <AdditionalItemsModal
                            additionalParkingItems={additionalParkingItems}
                            onCancelClick={onAdditionalServiceCancel}
                            onConfirmClick={onAdditionalServiceSelect}
                            onClose={onAdditionalServiceBackdropCancel}
                            selectedAdditionalService={selectedAdditionalService}
                            setSelectedAdditionalService={toggleAdditionalServiceCheckbox}
                            currency={selectedRateId.currencySymbol}
                        />
                    )}
                    {nonBookable && (
                        <span className="text-brand-secondary mt-3 d-block text-extra-small">
                            <T>booking.products.step.nonbookable.step.extra.description</T>
                        </span>
                    )}
                    {!nonBookable && (
                        <div className={classNames('row', styles.nextButtonContainer)}>
                            <Button
                                btnStyle="primary"
                                onClick={onNextStep}
                                disabled={areGetUpSellOptionPending || !selectedRateId || areAddtionalItemsPending}
                                isLoading={areGetUpSellOptionPending || areAddtionalItemsPending}
                                className={styles.nextButton}>
                                <T>subscription.purchase.label.next</T>
                            </Button>
                        </div>
                    )}
                </CardSection>
            )}
        </>
    )
}

export default compose(
    withRouter,
    withRouteParams,
    connect(
        createStructuredSelector({
            upSellOptions: upSellOptionsSelector,
            selectedUpSellOption: selectedUpSellOptionSelector,
            areUpgradeUpSellOptionPending: upgradeUpSellOptionPendingSelector,
            areGetUpSellOptionPending: getUpSellOptionPendingSelector,
            upgradeUpSellPendingError: upgradeUpSellErrorSelector,
            additionalParkingItems: additionalParkingItemsSelector,
            areAddtionalItemsPending: addtionalItemsPendingSelector,
            existingReservation: reservationViaUrlSelector,
        }),
        {
            fetchUpSellOptions,
            upgradeUpSellBookingDetails,
            selectUpSellOption,
            clearSelectedUpSellOption,
            clearPending,
            getAdditionalParkingItems,
        }
    )
)(withRates(ProductSelection))
